import React, { FC, useCallback, useMemo } from 'react';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';

import { Form, TerminalInput } from '~/components';
import { PaymentDetails } from '~/types';
import { paymentDetails } from '~/routes';
import AmountInput from '~/components/inputs/AmountInput';
import { useAllTerminals } from '~/hooks/terminals';
import { useAccountsByIdsQuery } from '~/hooks/accounts';
import { useWatch } from 'antd/es/form/Form';

interface PaymentDetailsFormProps {
  name: typeof paymentDetails;
  initialValues?: PaymentDetails;
  edit?: boolean;
}

const PaymentDetailsForm: FC<PaymentDetailsFormProps> = (props) => {
  const { name, initialValues } = props;

  const [form] = Form.useForm();

  const { data: terminalsData } = useAllTerminals();
  const selectedTerminalId = useWatch('terminalId', form);
  const selectedTerminalAccountsIds =
    terminalsData?.find(({ id }) => (selectedTerminalId ? id === selectedTerminalId : false))
      ?.accountIds ?? [];

  const { data: accountsByTerminalData } = useAccountsByIdsQuery(selectedTerminalAccountsIds, {
    enabled: selectedTerminalAccountsIds.length > 0 && !!selectedTerminalId,
  });
  const accountsByTerminal = accountsByTerminalData?.data;

  const availableCurrencies = useMemo(() => {
    if (!accountsByTerminal) return {};
    const currencies = new Set(
      accountsByTerminal.flatMap((acc) => (acc.currency ? [acc.currency] : []))
    );
    return Object.fromEntries([...currencies].map((s) => [s, true]));
  }, [accountsByTerminal]);

  const handleTerminalChange = useCallback(() => {
    form.setFieldValue(['amount', 'currency'], null);
  }, [form]);

  const [t] = useTranslation('paymentDetails');
  const dt = (key: string): string => t(`translation:${key}`);

  return (
    <Form form={form} layout="vertical" name={name} initialValues={initialValues}>
      <Form.Item name="name" label={dt('name')} rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="terminalId" label={t('terminal')} rules={[{ required: true }]}>
        <TerminalInput onChange={handleTerminalChange} />
      </Form.Item>
      <Form.Item label={t('amount')}>
        <AmountInput
          form={form}
          name="amount"
          currencyFilter={(currency: string) => !!availableCurrencies[currency]}
        />
      </Form.Item>
      <Form.Item name="comment" label={t('comment')}>
        <Input.TextArea allowClear />
      </Form.Item>
    </Form>
  );
};
export default PaymentDetailsForm;
